import {
  collection,
  onSnapshot,
  query,
  Unsubscribe,
  where,
} from "firebase/firestore";
import { collectionData } from "rxfire/firestore";
import { combineLatest, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { DocumentState } from "~/types/enums/DocumentState";
import { isCompanionSubmittedDocument } from "~/utils/submitted-document.utils";

export const useUserDocuments = (userId: string) =>
  defineStore(`/users/${userId}/documents`, () => {
    const appUserDataStore = useAppUserDataStore();
    const { numGradedDocuments, showOtherUsersSubmissions } =
      storeToRefs(appUserDataStore);

    const hasIniitalDocumentsDataLoaded = ref(false);

    const ungradedDocuments = ref<SubmittedDocuments>([]);
    const otherUserClassroomUngradedDocuments = ref<SubmittedDocuments>([]);

    const subscription = ref<Unsubscribe | undefined>();

    const isInitialized = ref(false);

    const initialize = () => {
      if (isInitialized.value) return;

      const db = useFirestore();
      const documentsRef = collection(db, `/documents`);
      const documentsQuery = query(
        documentsRef,
        where("userId", "==", userId),
        where("state", "not-in", [
          DocumentState.draft,
          DocumentState.editing,

          DocumentState.studentReview,
          DocumentState.graded,
          DocumentState.archived,
          DocumentState.ignored,
        ])
      );

      if (subscription.value) {
        subscription.value();
      }

      subscription.value = onSnapshot(documentsQuery, (snapshot) => {
        ungradedDocuments.value = snapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          } as SubmittedDocument;
        });

        hasIniitalDocumentsDataLoaded.value = true;
      });

      isInitialized.value = true;
    };

    const otherUserClassroomSubmissionsSubscription = ref<
      Subscription | undefined
    >();

    const initializeOtherUserClassroomSubmissions = ({
      classroomIds,
    }: {
      classroomIds: string[];
    }) => {
      otherUserClassroomSubmissionsSubscription.value?.unsubscribe();
      otherUserClassroomSubmissionsSubscription.value = undefined;
      otherUserClassroomUngradedDocuments.value = [];

      if (classroomIds.length === 0) return;

      const db = useFirestore();
      const submissionsRef = collection(db, `/documents`);

      const states = [
        DocumentState.submitted,
        DocumentState.notImported,
        DocumentState.conversionFailed,
        DocumentState.evaluating,
        DocumentState.pending,
        DocumentState.uploading,
        DocumentState.googlePermissionError,
        DocumentState.unsupportedFormat,
        DocumentState.grading,
        DocumentState.processing,
        DocumentState.converting,
      ];

      // Firestore multiplies the alternatives in both `in` filters and allows
      // at most 30 disjunctions per query (2 classrooms × 11 states = 22).
      const chunkSize = Math.floor(30 / states.length);
      const classroomIdChunks = [];
      for (let i = 0; i < classroomIds.length; i += chunkSize) {
        classroomIdChunks.push(classroomIds.slice(i, i + chunkSize));
      }

      // Create an observable for each chunk
      const observables = classroomIdChunks.map((chunk) => {
        const submissionsQuery = query(
          submissionsRef,
          where("classroomId", "in", chunk),
          where("state", "in", states)
        );

        return collectionData(submissionsQuery, {
          idField: "id",
        }).pipe(
          map((submissions) => {
            return submissions.map((submission) => {
              return {
                ...submission,
                id: submission.id,
              } as SubmittedDocument;
            });
          })
        );
      });

      // Combine all observables into one
      const combinedObservable = combineLatest(observables).pipe(
        map((results) => results.flat())
      );

      // Subscribe to the combined observable
      otherUserClassroomSubmissionsSubscription.value =
        combinedObservable.subscribe({
          next: (submissions) => {
            otherUserClassroomUngradedDocuments.value = submissions;
          },
          error: (error) => {
            otherUserClassroomUngradedDocuments.value = [];
            console.error("Error streaming shared classroom submissions", error);
            useBaseToast("Unable to load shared classroom submissions.", "error");
          },
        });
    };

    const allUngradedDocuments = computed(() => {
      return [
        ...ungradedDocuments.value,
        ...otherUserClassroomUngradedDocuments.value,
      ];
    });

    const homePageUngradedDocuments = computed(() => {
      const documents =
        showOtherUsersSubmissions.value == true
          ? allUngradedDocuments.value
          : ungradedDocuments.value;

      return documents.filter(
        (document) => !isCompanionSubmittedDocument(document)
      );
    });

    const classroomUngradedDocuments = computed(() => {
      return (classroomId: string) => {
        return allUngradedDocuments.value.filter(
          (doc) => doc.classroomId === classroomId
        );
      };
    });

    const assignmentUngradedDocuments = computed(() => {
      return (assignmentId: string) => {
        return allUngradedDocuments.value.filter(
          (doc) => doc.assignmentId === assignmentId
        );
      };
    });

    const hasOtherUserClassroomSubmissions = computed(() => {
      return otherUserClassroomUngradedDocuments.value.length > 0;
    });

    return {
      initialize,
      allUngradedDocuments,
      homePageUngradedDocuments,
      hasOtherUserClassroomSubmissions,
      classroomUngradedDocuments,
      assignmentUngradedDocuments,
      numGradedDocuments,
      hasIniitalDocumentsDataLoaded,
      initializeOtherUserClassroomSubmissions,
    };
  });
